import { ReactIntegration } from '@grafana/faro-react';
import { getBrowserInfo, initFaro, netlifyDeployMeta } from '@navio/observability';
import { noddiAsync } from 'noddi-async';

import { environment, isDevelopment, telemetryEnabled } from './src/constants/environment';
import { tracking } from './src/tracking';
import { tracePropagationPatterns } from './tracePropagation';

// Initialize Grafana Faro RUM and register it as a shipper alongside Mixpanel
// (via `@navio/observability`'s shared dispatcher). Call this in each app's
// `index.tsx` BEFORE `Sentry.init` so Faro's global error/console
// instrumentation is in place first. Faro and Sentry coexist (separate
// transports). Disabled on `local` so dev machines don't ship RUM.
//
// `VITE_APP_FARO_URL` / `VITE_APP_OPENPANEL_API_URL` are build-time defaults
// (`telemetry.noddi.co`, `analytics.noddi.co/api`). For Noddi dual-TLD hosts,
// `resolveNoddiDualTldUrl` rewrites `.co` ↔ `.no` from the page hostname so the
// same customer build works on `app.noddi.co`, `app.noddi.no`, and brand `.no`
// hosts without CORS errors. Non-matching hosts (Netlify preview, localhost)
// keep the configured URL.
// `VITE_APP_FARO_API_KEY` is sent by the FetchTransport as the `x-api-key`
// header that the ztrapi Traefik middleware validates. It is NOT a secret —
// it ships in browser JS; real origin control is the server-side CORS allow-list.
//
// PII scrubbing (email + Norwegian phone) is applied by default inside
// `initFaro`; only `id` + `isWorker` ever reach Faro on identify.
//
// Distributed tracing: Faro injects W3C `traceparent` into the active API
// client's origin and any explicit `VITE_APP_TRACE_PROPAGATE_ORIGINS` so the
// React fetch joins Traefik, the API, the cache, and Postgres as one Tempo trace.
// Backend CORS must accept `traceparent`/`tracestate`.
export function initObservability(appName: string, appVersion?: string) {
  const tracePropagationOrigins = resolveTracePropagationOrigins();

  // Netlify build metadata (site name, deploy/commit/build id, branch, context),
  // injected as `__DEPLOY_META__` by `packages/config/vite.config.mts`. Maps to
  // Faro `app` resource attributes (gitHash / namespace / bundleId) + session
  // attributes. Empty `{}` on local/dev where the Netlify vars are unset.
  const deployMeta = netlifyDeployMeta(__DEPLOY_META__);

  initFaro({
    appName,
    version: appVersion,
    url: import.meta.env.VITE_APP_FARO_URL,
    apiKey: import.meta.env.VITE_APP_FARO_API_KEY,
    environment,
    gitHash: deployMeta.gitHash,
    namespace: deployMeta.namespace,
    bundleId: deployMeta.bundleId,
    runtime: deployMeta.runtime,
    sessionAttributes: deployMeta.sessionAttributes,
    enabled: telemetryEnabled,
    sessionSampleRate: isDevelopment ? 1.0 : 0.2,
    instrumentations: [new ReactIntegration()],
    // Per-fetch RUM events and console capture stay off (see initFaro).
    tracePropagationUrls: tracePropagationOrigins
  });

  // Super properties on every event: which app and which release. `appVersion`
  // makes it possible to attribute a funnel or error regression to a deploy.
  // Replayed onto Mixpanel when it registers later (customer app). For Grafana the
  // canonical `app`/`version` labels come from the Faro `app` meta above (resource
  // attributes), not from these super properties — they remain for Mixpanel.
  // `environment` (local/development/production) is a super property on every
  // event so prod and dev (deploy-preview) signals are distinguishable in the
  // Mixpanel & OpenPanel dashboards — filter/segment by it.
  // `runtime` (e.g. "netlify") is registered for product analytics parity so
  // platform can be used in funnels/filters outside Grafana too.
  //
  // `os` + `browser` (via getBrowserInfo from @navio/observability) are attached
  // as super properties so product analytics events include client environment.
  tracking.registerBoot({
    app: appName,
    environment,
    ...(appVersion && { appVersion }),
    ...(deployMeta.runtime && { runtime: deployMeta.runtime }),
    ...getBrowserInfo()
  });
}

function resolveTracePropagationOrigins(): RegExp[] {
  return tracePropagationPatterns(import.meta.env.VITE_APP_TRACE_PROPAGATE_ORIGINS as string | undefined, () =>
    noddiAsync.getBaseUrl()
  );
}
